import { useEffect, useState } from "react";
import { CheckCircle2, Hammer, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useSpace } from "@/context/SpaceContext";

const POLL_MS = 2000, MAX_POLLS = 60;

const Step = ({ done, active, label, testId }) => (
  <li className="flex min-w-0 items-center gap-1.5" data-testid={testId} data-done={done ? "1" : "0"}>
    {done ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" /> : active ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[var(--gold)]" /> : <span className="h-4 w-4 shrink-0 rounded-full border border-neutral-600" />}
    <span className={`break-words ${done ? "text-emerald-200" : "text-neutral-400"}`}>{label}</span>
  </li>
);

// Official upload: live « Chargé → Vérifié → Publié en page d'accueil » (automatic, no confirmation), or why it is blocked.
export const PublishProgress = ({ appId, testId = "publish-progress" }) => {
  const { t } = useI18n();
  const { base } = useSpace();
  const [s, setS] = useState(null);
  useEffect(() => {
    let n = 0, stop = false, timer;
    const tick = async () => {
      try {
        const { data } = await api.get(`${base}/apps/${appId}/review`);
        if (stop) return;
        setS(data);
        const blocked = data.status === "pending" && data.checked_at && data.blocking?.length;
        if (data.status === "published" || blocked || data.hold) return;
      } catch {
        if (stop) return;
      }
      if (++n < MAX_POLLS) timer = setTimeout(tick, POLL_MS);
    };
    tick();
    return () => { stop = true; clearTimeout(timer); };
  }, [appId, base]);
  const published = s?.status === "published";
  const verified = published || Boolean(s?.checked_at && !s?.blocking?.length);
  const blocked = !published && s?.checked_at && s?.blocking?.length > 0;
  return (
    <div className="mt-3 min-w-0 space-y-1.5 text-xs" data-testid={testId}>
      <ol className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
        <Step done label={t("pub.loaded")} testId={`${testId}-loaded`} />
        <Step done={verified} active={!verified && !blocked && !s?.hold} label={t("pub.verified")} testId={`${testId}-verified`} />
        <Step done={published} active={verified && !published && !s?.hold} label={t(base === "/admin" ? "pub.home" : "pub.live")} testId={`${testId}-published`} />
      </ol>
      {blocked && <p className="flex items-start gap-1.5 break-words text-red-300" data-testid={`${testId}-blocked`}><XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {t("review.blockedBy", { list: s.blocking.map((c) => `${t(`review.c.${c.id}`)}${c.detail ? ` (${c.detail})` : ""}`).join(" · ") })}</p>}
      {s?.hold && !published && <p className="flex items-start gap-1.5 break-words text-amber-200" data-testid={`${testId}-atelier`}><Hammer className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {t("review.held")}</p>}
    </div>
  );
};
